'use es6';

import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import useCustomReportLimit from '../../access/limits/hooks/useCustomReportLimit';
import { useHasReportingCreateAndOwnAccess, useHasReportingEditAccess } from '../../access/scopes/hooks/useHasScope';
import useUserInfo from '../../access/useUserInfo';
import { useCustomReportCount, useReportCounts } from '../../data/hooks/useReportCounts';
import { getRestrictedFieldLevelProperties } from '../../data/schemas/reportSchema';
import { hasSufficientDashboardEditPermission } from '../../lib/dashboardLogic';
import { getUpgradeUrl } from '../../lib/linkUrls';
import { hasSufficientReportEditPermission, isCustom } from '../../lib/report-logic';
import { CUSTOM_REPORT_LIMIT_EXCEEDED, DASHBOARD_PERMISSION_DENIED, INSUFFICIENT_ASSET_PERMISSION_LEVEL, INSUFFICIENT_REPORTING_PERMISSION_LEVEL, LACKS_FIELD_ACCESS } from '../problems';
import { CREATE_NEW_REPORT, EDIT_EXISTING_REPORT } from '../saveReport/saveNewReportTypes';
import RestrictedFieldsProblemMessage from '../shared/RestrictedFieldsProblemMessage';
import { getBulkActionProblem } from '../shared/bulkActionHelpers';
import { DATA_FETCHING_ERROR_PROBLEM_AND_MESSAGE, LOADING_PROBLEM_AND_MESSAGE } from '../shared/dataProblemsAndMessages';
import { Link } from 'foundations-components/transitional/components/Link';
import { jsx as _jsx } from "react/jsx-runtime";
const useAddReportsToAnyDashboardProblem = ({
  reports,
  currentDashboard,
  saveReportUseType = EDIT_EXISTING_REPORT // Default to checking for reporting edit permission level access.
}) => {
  const {
    loading: reportCountsLoading,
    error: reportCountsError
  } = useReportCounts();
  const {
    loading: userInfoLoading,
    error: userInfoError
  } = useUserInfo(false);
  const customReportCount = useCustomReportCount();
  const customReportLimit = useCustomReportLimit();
  const hasReportingEditAccess = useHasReportingEditAccess();
  const hasReportingCreateAndOwnAccess = useHasReportingCreateAndOwnAccess();
  const hasExceededCustomReportLimit = customReportCount > customReportLimit;
  if (reportCountsLoading || userInfoLoading) {
    return LOADING_PROBLEM_AND_MESSAGE;
  }
  if (reportCountsError || userInfoError) {
    return DATA_FETCHING_ERROR_PROBLEM_AND_MESSAGE;
  }
  if (saveReportUseType === EDIT_EXISTING_REPORT && !hasReportingEditAccess) {
    return {
      problem: INSUFFICIENT_REPORTING_PERMISSION_LEVEL,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.addReportToDashboard.button.tooltip.insufficientReportingPermission"
      })
    };
  }
  if (saveReportUseType === CREATE_NEW_REPORT && !hasReportingCreateAndOwnAccess) {
    return {
      problem: INSUFFICIENT_REPORTING_PERMISSION_LEVEL,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.addReportToDashboard.button.tooltip.insufficientReportingCreatePermission"
      })
    };
  }
  if (currentDashboard) {
    const dashboardPermissionDenied = !hasSufficientDashboardEditPermission(currentDashboard);
    if (dashboardPermissionDenied) {
      return {
        problem: DASHBOARD_PERMISSION_DENIED,
        disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.placeReport.tooltip.dashboardPermissionDenied"
        })
      };
    }
  }
  return getBulkActionProblem({
    assets: reports,
    getProblem: report => {
      const restrictedFieldLevelProperties = getRestrictedFieldLevelProperties(report);
      if (!hasSufficientReportEditPermission(report)) {
        return {
          problem: INSUFFICIENT_ASSET_PERMISSION_LEVEL,
          disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "reporting-action-components.actions.addReportToDashboard.button.tooltip.insufficientAssetPermission"
          })
        };
      }
      if (restrictedFieldLevelProperties.length) {
        return {
          problem: LACKS_FIELD_ACCESS,
          disabledMessage: /*#__PURE__*/_jsx(RestrictedFieldsProblemMessage, {
            message: 'reporting-action-components.actions.addReportToDashboard.button.tooltip.restrictedFieldLevelPermission',
            restrictedFieldLevelProperties: restrictedFieldLevelProperties
          })
        };
      }
      if (isCustom(report) && hasExceededCustomReportLimit) {
        return {
          problem: CUSTOM_REPORT_LIMIT_EXCEEDED,
          disabledMessage: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "reporting-action-components.actions.addReportToDashboard.button.tooltip.customReportLimitExceeded_jsx",
            elements: {
              UILink: Link
            },
            options: {
              href: getUpgradeUrl(),
              limit: customReportLimit
            }
          })
        };
      }
      return {
        problem: null,
        disabledMessage: null
      };
    }
  });
};
export default useAddReportsToAnyDashboardProblem;